/** NFA record detail — slide-over with the approval ladder, documents,
 *  revisions and the SAP BTP sync state. Approvers can act from here. */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Paperclip, Upload, GitCompare, ShieldCheck, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useNfaTheme, money, day, stamp } from './nfaTheme';
import { Btn, Drawer, Empty, Field, Input, Panel, Pill, Select } from './NfaPrimitives';
import { NfaNoteAnalysis } from './NfaCognara';

const LevelRow = ({ lv, T, canAct, onAct, busy }) => {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState('');
  const [delegate, setDelegate] = useState('');
  return (
    <div className="px-3 py-2.5" style={{ borderTop: `1px solid ${T.border}` }}
         data-testid={`nfa-level-${lv.level_no}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className="w-6 h-6 rounded-md grid place-items-center text-[11px] font-bold tabular-nums"
          style={{ background: T.tint(T.primary), color: T.primary }}>{lv.level_no}</span>
        <div className="min-w-0">
          <div className="text-[12px] font-semibold" style={{ color: T.ink }}>{lv.approver_role}</div>
          <div className="text-[10px]" style={{ color: T.muted }}>
            {(lv.approver_names || lv.approver_user_ids || []).join(', ') || '— unassigned —'}
            {lv.parallel_mode === 'ANY' ? ' · any one may approve' : ''}
            {lv.delegate_user_id ? ' · delegated' : ''}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2.5 text-[10px] tabular-nums"
             style={{ color: T.muted }}>
          <span>band {money(lv.threshold_min)}–{lv.threshold_max ? money(lv.threshold_max) : '∞'}</span>
          <span>SLA {lv.sla_days}d</span>
          {lv.days_pending != null && (
            <span style={{ color: lv.is_overdue ? T.red : T.body }}>{lv.days_pending}d pending</span>
          )}
          {lv.is_overdue && <Pill value="OVERDUE" T={{ ...T, tint: T.tint }} testId={`nfa-level-${lv.level_no}-overdue`} />}
          <Pill value={lv.action} T={T} testId={`nfa-level-${lv.level_no}-status`} />
        </div>
      </div>
      {lv.comment && (
        <p className="text-[11px] mt-1.5 pl-9" style={{ color: T.body }}>
          “{lv.comment}” · {stamp(lv.acted_on)}
        </p>
      )}
      {lv.skip_reason && (
        <p className="text-[10px] mt-1 pl-9" style={{ color: T.muted }}>Skipped — {lv.skip_reason}</p>
      )}
      {canAct && (
        <div className="pl-9 mt-2 space-y-2">
          {!open ? (
            <Btn T={T} variant="primary" onClick={() => setOpen(true)} testId="nfa-act-open">
              Act on level {lv.level_no}
            </Btn>
          ) : (
            <>
              <Input T={T} placeholder="Comment (required to reject or return)" value={comment}
                     onChange={(e) => setComment(e.target.value)} data-testid="nfa-act-comment" />
              <Input T={T} placeholder="Delegate to user_id (only for Delegate)" value={delegate}
                     onChange={(e) => setDelegate(e.target.value)} data-testid="nfa-act-delegate" />
              <div className="flex items-center gap-2 flex-wrap">
                <Btn T={T} variant="ok" disabled={busy} testId="nfa-act-approve"
                     onClick={() => onAct('APPROVED', comment, null)}>Approve</Btn>
                <Btn T={T} variant="danger" disabled={busy || !comment} testId="nfa-act-reject"
                     onClick={() => onAct('REJECTED', comment, null)}>Reject</Btn>
                <Btn T={T} variant="warn" disabled={busy || !comment} testId="nfa-act-return"
                     onClick={() => onAct('RETURNED', comment, null)}>Return for revision</Btn>
                <Btn T={T} variant="ghost" disabled={busy || !delegate} testId="nfa-act-delegate-btn"
                     onClick={() => onAct('DELEGATED', comment, delegate)}>Delegate</Btn>
                {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: T.muted }} />}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export const NfaDetailDrawer = ({ nfaId, onClose, onChanged }) => {
  const T = useNfaTheme();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cmp, setCmp] = useState(null);
  const fileRef = useRef(null);
  const [category, setCategory] = useState('Quotation');
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => {
    if (!nfaId) return;
    api.get(`/nfa/${nfaId}`).then((r) => setData(r.data))
      .catch(() => toast.error('Could not load that NFA'));
  }, [nfaId]);
  useEffect(() => { load(); }, [load]);

  const nfa = data?.nfa;
  const perm = data?.permissions || {};

  const act = async (action, comment, delegate) => {
    setBusy(true);
    try {
      await api.post(`/nfa/${nfaId}/action`, {
        level_no: perm.actLevel, action, comment: comment || null,
        delegate_user_id: delegate || null });
      toast.success(`Level ${perm.actLevel} ${action.toLowerCase()}`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/nfa/${nfaId}/submit`);
      toast.success(`Submitted — ${r.data.nfa.total_levels} approval levels`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Submit failed'); }
    finally { setBusy(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_category', category);
    setBusy(true);
    try {
      const r = await api.post(`/nfa/${nfaId}/attachments`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.filename} stored as v${r.data.version}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setBusy(false); }
  };

  const compare = async (a, b) => {
    const r = await api.get(`/nfa/${nfaId}/revisions/compare?a=${a}&b=${b}`);
    setCmp(r.data);
  };

  return (
    <Drawer open={!!nfaId} onClose={onClose} T={T} testId="nfa-detail-drawer"
      title={nfa ? `${nfa.nfa_no} — ${nfa.title}` : 'Loading…'}
      subtitle={nfa && (
        <div className="flex items-center gap-2 mt-1 flex-wrap text-[10.5px]" style={{ color: T.muted }}>
          <Pill value={nfa.status} T={T} testId="nfa-detail-status" />
          <span>{nfa.nfa_type}</span><span>·</span>
          <span>{nfa.project_name || nfa.project_id}</span><span>·</span>
          <span>WBS {nfa.wbs_element}</span><span>·</span>
          <span className="tabular-nums">v{nfa.version_no}</span>
          {nfa.status === 'UNDER_REVIEW' && (
            <span className="tabular-nums">at level {nfa.current_level} · {nfa.total_levels} levels in route</span>
          )}
        </div>
      )}>
      {!data ? (
        <div className="py-16 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto"
          style={{ color: T.primary }} /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[['Requested', money(nfa.requested_amount, nfa.currency)],
              ['Raised by', nfa.raised_by_name], ['Raised on', day(nfa.raised_on)],
              ['Required by', day(nfa.required_by_date)]].map(([l, v]) => (
              <div key={l} className="rounded-lg px-3 py-2"
                style={{ background: T.surface, border: `1px solid ${T.border}` }}>
                <div className="text-[9px] uppercase tracking-[0.08em]" style={{ color: T.muted }}>{l}</div>
                <div className="text-[12.5px] font-semibold tabular-nums" style={{ color: T.ink }}>{v || '—'}</div>
              </div>
            ))}
          </div>

          {perm.canSubmit && (
            <Panel T={T} testId="nfa-submit-panel" title="Ready to route">
              <p className="text-[11.5px] mb-2" style={{ color: T.body }}>
                Submitting resolves the approval matrix for this project, type and amount and
                routes the note level by level.
              </p>
              <Btn T={T} onClick={submit} disabled={busy} testId="nfa-submit-btn">
                {busy ? 'Submitting…' : 'Submit for approval'}
              </Btn>
            </Panel>
          )}

          <Panel T={T} pad={false} testId="nfa-ladder"
            title="Approval route"
            subtitle={perm.makerCheckerBlocked
              ? 'Maker-checker: you raised this note, so you cannot approve it at any level'
              : 'Resolved from the approval matrix at submission'}>
            {(data.levels || []).length === 0
              ? <Empty T={T} testId="nfa-no-levels">Not routed yet — submit the note to build the chain.</Empty>
              : data.levels.map((lv) => (
                <LevelRow key={lv.levelId} lv={lv} T={T} busy={busy}
                  canAct={perm.canAct && perm.actLevel === lv.level_no} onAct={act} />
              ))}
          </Panel>

          <Panel T={T} testId="nfa-justification" title="Justification & business case">
            <p className="text-[11.5px] leading-relaxed whitespace-pre-wrap" style={{ color: T.body }}>
              {nfa.justification || '—'}
            </p>
            <p className="text-[11.5px] leading-relaxed whitespace-pre-wrap mt-3 pt-3"
               style={{ color: T.body, borderTop: `1px solid ${T.border}` }}>
              {nfa.business_case || '—'}
            </p>
          </Panel>

          <Panel T={T} testId="nfa-attachments" title="Supporting documents"
            subtitle="Re-uploading the same filename creates a new version — nothing is overwritten"
            right={<>
              <Select T={T} value={category} onChange={(e) => setCategory(e.target.value)}
                data-testid="nfa-doc-category">
                {['Quotation', 'Comparative Statement', 'Technical Eval', 'Contract Draft',
                  'Budget Sheet', 'Correspondence', 'Other'].map((c) => <option key={c}>{c}</option>)}
              </Select>
              <input ref={fileRef} type="file" className="hidden" data-testid="nfa-file-input"
                onChange={(e) => upload(e.target.files?.[0])} />
              <Btn T={T} variant="ghost" onClick={() => fileRef.current?.click()}
                testId="nfa-upload-btn"><Upload className="w-3.5 h-3.5" /> Upload</Btn>
            </>}>
            {(data.attachments || []).length === 0
              ? <Empty T={T} testId="nfa-no-attachments">No documents attached yet.</Empty>
              : (
                <div className="space-y-1.5">
                  {data.attachments.map((a) => (
                    <div key={a.attachmentId} className="flex items-center gap-2.5 text-[11px] py-1.5"
                      style={{ borderBottom: `1px solid ${T.border}` }}
                      data-testid={`nfa-attachment-${a.attachmentId}`}>
                      <Paperclip className="w-3.5 h-3.5 shrink-0" style={{ color: T.muted }} />
                      <button type="button" className="truncate text-left hover:underline"
                        style={{ color: T.primary }}
                        onClick={() => setPreview(a)}>{a.filename}</button>
                      <span className="px-1.5 py-0.5 rounded text-[9.5px] shrink-0"
                        style={{ background: T.tint(T.primary), color: T.primary }}>v{a.version}</span>
                      <span className="shrink-0" style={{ color: T.muted }}>{a.doc_category}</span>
                      <span className="ml-auto tabular-nums shrink-0" style={{ color: T.muted }}>
                        {(a.size / 1024).toFixed(0)} KB · {a.extraction_method} · {stamp(a.uploaded_on)}
                      </span>
                      <ShieldCheck className="w-3.5 h-3.5 shrink-0"
                        style={{ color: a.scan?.scanned ? T.green : T.muted }}
                        title={a.scan?.detail} />
                    </div>
                  ))}
                </div>
              )}
            {preview && (
              <div className="mt-3 rounded-lg overflow-hidden" style={{ border: `1px solid ${T.border}` }}
                   data-testid="nfa-attachment-preview">
                <div className="px-3 py-1.5 flex items-center gap-2 text-[10.5px]"
                     style={{ background: T.surfaceAlt, color: T.body }}>
                  {preview.filename}
                  <button type="button" className="ml-auto" onClick={() => setPreview(null)}
                    style={{ color: T.muted }}>close</button>
                </div>
                {(preview.mime_type || '').includes('pdf') ? (
                  <iframe title="preview" src={`${process.env.REACT_APP_BACKEND_URL}/api/nfa/files/${preview.storage_key}`}
                    className="w-full" style={{ height: 420, background: '#fff' }} />
                ) : (preview.mime_type || '').startsWith('image/') ? (
                  <img alt={preview.filename} className="w-full"
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/nfa/files/${preview.storage_key}`} />
                ) : (
                  <div className="p-4 text-[11px]" style={{ color: T.muted }}>
                    No inline preview for this type — use the link to download.
                  </div>
                )}
              </div>
            )}
          </Panel>

          <Panel T={T} testId="nfa-revisions" title="Revision history"
            subtitle="Immutable — every edit, submission and return is recorded">
            <div className="space-y-1.5">
              {(data.revisions || []).map((r, i) => (
                <div key={r.revisionId} className="flex items-center gap-2 text-[11px] py-1.5"
                  style={{ borderBottom: `1px solid ${T.border}` }}
                  data-testid={`nfa-revision-${r.revisionId}`}>
                  <span className="px-1.5 py-0.5 rounded text-[9.5px] tabular-nums"
                    style={{ background: T.tint(T.primary), color: T.primary }}>v{r.version_no}</span>
                  <span className="truncate" style={{ color: T.body }}>{r.change_summary}</span>
                  <span className="ml-auto tabular-nums shrink-0" style={{ color: T.muted }}>{stamp(r.changed_on)}</span>
                  {i > 0 && (
                    <button type="button" title="Compare with the previous version"
                      onClick={() => compare(data.revisions[i - 1].revisionId, r.revisionId)}
                      data-testid={`nfa-compare-${r.revisionId}`}>
                      <GitCompare className="w-3.5 h-3.5" style={{ color: T.primary }} />
                    </button>
                  )}
                  <button type="button" className="text-[10px] hover:underline" style={{ color: T.primary }}
                    data-testid={`nfa-restore-${r.revisionId}`}
                    onClick={async () => {
                      await api.post(`/nfa/${nfaId}/revisions/${r.revisionId}/restore`);
                      toast.success(`Version ${r.version_no} restored as a new draft`);
                      onChanged?.();
                    }}>restore</button>
                </div>
              ))}
            </div>
            {cmp && (
              <div className="mt-3 rounded-lg overflow-hidden" style={{ border: `1px solid ${T.border}` }}
                   data-testid="nfa-revision-diff">
                <div className="grid grid-cols-3 px-3 py-1.5 text-[9.5px] uppercase tracking-[0.08em]"
                  style={{ background: T.surfaceAlt, color: T.muted }}>
                  <div>Field</div><div>v{cmp.a.version_no} · {stamp(cmp.a.changed_on)}</div>
                  <div>v{cmp.b.version_no} · {stamp(cmp.b.changed_on)}</div>
                </div>
                {Object.keys(cmp.diff).length === 0 ? (
                  <div className="p-3 text-[11px]" style={{ color: T.muted }}>No field changed between these versions.</div>
                ) : Object.entries(cmp.diff).map(([k, v]) => (
                  <div key={k} className="grid grid-cols-3 px-3 py-1.5 text-[10.5px]"
                    style={{ borderTop: `1px solid ${T.border}` }}>
                    <div style={{ color: T.ink }}>{k}</div>
                    <div className="truncate" style={{ color: T.red }}>{String(v.before ?? '—').slice(0, 90)}</div>
                    <div className="truncate" style={{ color: T.green }}>{String(v.after ?? '—').slice(0, 90)}</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          <NfaNoteAnalysis nfaId={nfaId} T={T} />

          <Panel T={T} testId="nfa-btp-panel" title="SAP BTP"
            right={<Btn T={T} variant="ghost" testId="nfa-btp-push"
              onClick={async () => {
                const r = await api.post(`/nfa/connectors/sap-btp/push/${nfaId}`);
                toast[r.data.ok ? 'success' : 'error'](
                  r.data.ok ? `Pushed — ${r.data.response?.externalRef || 'updated'}`
                            : r.data.error || 'Push failed');
                load();
              }}><RefreshCw className="w-3.5 h-3.5" /> Push now</Btn>}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px]">
              {[['External ref', nfa.external_ref || '—'], ['Sync', nfa.sync_status],
                ['BTP status', nfa.btp_status || '—'],
                ['Last sync', stamp(nfa.last_sync_at)]].map(([l, v]) => (
                <div key={l}>
                  <div className="text-[9px] uppercase tracking-[0.08em]" style={{ color: T.muted }}>{l}</div>
                  <div className="font-semibold tabular-nums truncate" style={{ color: T.ink }}>{v}</div>
                </div>
              ))}
            </div>
            {nfa.conflict && (
              <p className="text-[11px] mt-2 px-2.5 py-2 rounded"
                style={{ background: T.tint(T.accent), color: T.accent }} data-testid="nfa-conflict">
                Conflict — local v{nfa.conflict.localVersion} vs BTP v{nfa.conflict.remoteVersion}.
                {' '}{nfa.conflict.detail}
              </p>
            )}
          </Panel>

          {perm.canWithdraw && (
            <Btn T={T} variant="danger" testId="nfa-withdraw-btn" onClick={async () => {
              await api.post(`/nfa/${nfaId}/withdraw`, { reason: 'Withdrawn by originator' });
              toast.success('NFA withdrawn'); load(); onChanged?.();
            }}>Withdraw this NFA</Btn>
          )}
        </>
      )}
    </Drawer>
  );
};

export default NfaDetailDrawer;
